import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HeartPulse, Music, Briefcase, ChevronRight, ChevronLeft } from 'lucide-react';
export function CampusLifeSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const slides = [{
    id: 1,
    title: 'Student Welfare & Health',
    desc: 'Full-time free medical services, mental health initiatives, and stress management counseling.',
    icon: <HeartPulse className="w-6 h-6" data-id="element-216" />,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2070&auto=format&fit=crop'
  }, {
    id: 2,
    title: 'Vibrant Co-Curriculars',
    desc: "Annual cultural festivals like 'Spring in the Air', competitive club teams, and art exhibitions.",
    icon: <Music className="w-6 h-6" data-id="element-217" />,
    image: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?q=80&w=2070&auto=format&fit=crop'
  }, {
    id: 3,
    title: 'Career Alignment Center',
    desc: 'Dedicated placement cells, industry networking events, and resume building workshops.',
    icon: <Briefcase className="w-6 h-6" data-id="element-218" />,
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2084&auto=format&fit=crop'
  }, {
    id: 4,
    title: 'State-of-the-art Library',
    desc: 'Extensive digital and physical collections with quiet study zones and collaborative spaces.',
    icon: <Music className="w-6 h-6" data-id="element-219" />,
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop'
  }];
  return <section className="py-24 overflow-hidden relative bg-white" data-id="element-220">
      <div className="max-w-7xl mx-auto px-6 mb-12 flex justify-between items-end" data-id="element-221">
        <div data-id="element-222">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-navy" data-id="element-223">
            Campus Life & Culture
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl" data-id="element-224">
            A rich ecosystem outside classrooms designed to nurture creativity,
            well-being, and professional growth.
          </p>
        </div>
        <div className="hidden md:flex space-x-3" data-id="element-225">
          <button onClick={() => scroll('left')} className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-600" data-id="element-226">
            <ChevronLeft className="w-5 h-5" data-id="element-227" />
          </button>
          <button onClick={() => scroll('right')} className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-600" data-id="element-228">
            <ChevronRight className="w-5 h-5" data-id="element-229" />
          </button>
        </div>
      </div>

      {/* Horizontal Scroll Container */}
      <div ref={scrollRef} className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory px-6 pb-12 space-x-6" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-230">
        {/* Spacer for initial alignment */}
        <div className="w-[calc((100vw-80rem)/2)] shrink-0 hidden xl:block" data-id="element-231" />

        {slides.map((slide, i) => <motion.div key={slide.id} initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: i * 0.1
      }} className="w-[300px] md:w-[400px] lg:w-[500px] shrink-0 snap-center relative rounded-3xl overflow-hidden group h-[400px] shadow-md" data-id="element-232">
            <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-233" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent opacity-90" data-id="element-234" />

            <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-235">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-6 text-navy transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 shadow-lg" data-id="element-236">
                {slide.icon}
              </div>
              <h3 className="text-2xl font-bold mb-2 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 text-white" data-id="element-237">
                {slide.title}
              </h3>
              <p className="text-white/80 text-sm transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 delay-75" data-id="element-238">
                {slide.desc}
              </p>
            </div>
          </motion.div>)}

        {/* Spacer for end alignment */}
        <div className="w-[calc((100vw-80rem)/2)] shrink-0 hidden xl:block" data-id="element-239" />
      </div>
    </section>;
}